import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Checkbox } from '@/components/form/inputs/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/dialog';
import { UserRole, UserRoleSchema } from '@/entities/user-role';

import type { Role } from '../entities/role';
import { EditRoleMutation } from '../mutations/edit-role';
import { GetSitesQuery } from '../queries/get-sites';

export type EditRoleDialogProps = {
  role: Role | undefined;
  onClose: () => void;
};

export function EditRoleDialog({ role, onClose }: EditRoleDialogProps) {
  return (
    <Dialog open={Boolean(role)} onClose={onClose}>
      {role ? <EditRoleForm role={role} onClose={onClose} /> : null}
    </Dialog>
  );
}

type EditRoleFormProps = {
  role: Role;
  onClose: () => void;
};

const FormSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Required'),
  permission: UserRoleSchema,
  siteIds: z.array(z.string()).min(1, 'Required').nullable(),
});

type FormInputs = z.infer<typeof FormSchema>;

const permissions = Object.values(UserRole);

enum SiteIdsMode {
  ALL = 'all',
  SELECTED = 'selected',
}

export function EditRoleForm({ role, onClose }: EditRoleFormProps) {
  const sitesQuery = GetSitesQuery.useQuery();
  const editRole = EditRoleMutation.useMutation();

  const form = useForm<FormInputs>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      id: role.id,
      name: role.name,
      permission: role.permission,
      siteIds: role.sites?.map((site) => site.id) ?? null,
    },
  });

  function handleClose() {
    onClose();
  }

  async function handleSubmit(data: FormInputs) {
    await toast.promise(editRole.mutateAsync(data), {
      pending: 'Editing role...',
      success: 'Role updated successfully!',
      error: 'Failed to update role. Please try again.',
    });
    handleClose();
    form.reset();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogBody className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="permission"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Permission</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select permission" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {permissions.map((permission) => (
                          <SelectItem key={permission} value={permission}>
                            {permission}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="siteIds"
            render={({ field }) => {
              const mode = Array.isArray(field.value)
                ? SiteIdsMode.SELECTED
                : SiteIdsMode.ALL;

              const selectedSiteIds = Array.isArray(field.value)
                ? Object.fromEntries(field.value.map((id) => [id, true]))
                : {};

              return (
                <FormItem>
                  <FormLabel>Sites</FormLabel>
                  <FormControl>
                    <>
                      <Select
                        value={mode}
                        onValueChange={(mode) => {
                          if (mode === SiteIdsMode.ALL) {
                            field.onChange(null);
                          } else if (mode === SiteIdsMode.SELECTED) {
                            field.onChange([]);
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value={SiteIdsMode.ALL}>
                              All sites
                            </SelectItem>
                            <SelectItem value={SiteIdsMode.SELECTED}>
                              Selected Sites
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {mode === SiteIdsMode.SELECTED ? (
                        <div className="mt-4 space-y-2">
                          {(sitesQuery.data?.sites ?? []).map((site) => (
                            <div
                              key={site.id}
                              className="flex items-center space-x-3"
                            >
                              <Checkbox
                                id={`site-${site.id}`}
                                checked={selectedSiteIds[site.id] ?? false}
                                onChange={(e) => {
                                  const currentIds = Array.isArray(field.value)
                                    ? field.value
                                    : [];
                                  if (e.target.checked) {
                                    field.onChange([...currentIds, site.id]);
                                  } else {
                                    field.onChange(
                                      currentIds.filter((id) => id !== site.id),
                                    );
                                  }
                                }}
                              />
                              <label
                                htmlFor={`site-${site.id}`}
                                className="cursor-pointer text-sm font-medium text-gray-700"
                              >
                                {site.fullFrontendId}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" type="button" onClick={handleClose}>
            cancel
          </Button>
          <Button
            className="bg-primary-500 hover:bg-primary-400"
            type="submit"
            disabled={editRole.isLoading}
          >
            {editRole.isLoading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Editing
              </>
            ) : (
              'Edit'
            )}
          </Button>
        </DialogActions>
      </form>
    </Form>
  );
}
